import {_} from 'lodash';
import * as ko from "knockout";
import {ICompanySyncstep, ICompanySyncstepParameter, IExportAttribute} from "../../../../../index";
import {Abstract} from "./Abstract";
import {Config} from "./Config";
import {ErrorManager} from "../../../../../error/ErrorManager";
import {createErrorMessage} from "../../../../../util/misc";
import {getDate} from "../../../../../util/datetime";
import editHtml from './edit.html?raw';

export const template = editHtml;

interface IParams {
    companysyncstep: ICompanySyncstep
    mutableSyncstepConfigs: KnockoutObservableArray<Config>
    errorManager: ErrorManager
}

export class viewModel extends Abstract {

    private mutableSyncstepConfigs: KnockoutObservableArray<Config>
    private errorManager: ErrorManager;
    private isPasswordToggled: KnockoutObservable<boolean> = ko.observable(false);

    constructor(params: IParams) {
        super(params);
        this.mutableSyncstepConfigs = params.mutableSyncstepConfigs
        this.errorManager = params.errorManager
        this.init()
    }


    protected override init(): Promise<void> {
        this.mutableSyncstepConfigs([])
        return super
            .init()
            .then(() => {
                this.companySyncstepParameters.forEach((o: ICompanySyncstepParameter) => {
                    const c = new Config(o, this.availableSyncstepParameters, this.messageFlowOptions)
                    this.mutableSyncstepConfigs.push(c)
                    this.errorManager.addErrorObject(c.eo_key)
                    this.errorManager.addErrorObject(c.eo_value)
                })
            })
            .then(() => this.loadClientCredentialContext())
            .then(() => this.loadMessageFlowContext())
            .then(() => {
                this.messageData(null)
                this.isReady(true)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private isClientCredentialRefConfig(config: Config): boolean {
        return this.isClientCredentialRefKey(config.key());
    }

    private getEditorType(config: Config): string {
        if (this.isClientCredentialRefConfig(config)) {
            return 'clientcredentialref';
        }
        return String(this.getFormatType(config.key()) ?? '').toLowerCase();
    }

    private getClientCredentialHintText(selectedValue: string | boolean): string {
        const selectedId = selectedValue == null ? '' : String(selectedValue);
        if (!selectedId) {
            return 'Select a credential to view details';
        }

        const credential = this.getClientCredentialOptionById(selectedId);
        if (!credential) {
            return `Credential #${selectedId} not found in available options`;
        }

        const expiry = credential.expiryDate ? getDate(credential.expiryDate, true) : '—';
        const description = credential.description?.trim() || '—';
        const clientIdOrUsername = credential.clientIdOrUsername?.trim() || '—';
        const fingerprint = credential.fingerprint?.trim() || '—';

        return [
            `Name: ${credential.name || `#${credential.id}`}`,
            `Description: ${description}`,
            `Expiry: ${expiry}`,
            `Client ID/Username: ${clientIdOrUsername}`,
            `Secret fingerprint: ${fingerprint}`,
        ].join('\n');
    }

    private getAvailableSyncstepKeys(allowKey: IExportAttribute['name']): IExportAttribute['name'][] {
        const used = this.getMutableSyncstepConfigs();
        const usedKeys = used.map(o => o.key())
        return _(this.availableSyncstepParameters)
            .map((o: IExportAttribute) => o.name)
            .filter((key: IExportAttribute['name']) => {
                if (key == allowKey) {
                    return true
                }
                return !usedKeys.find(u => u == key)
            })
            .sortBy((str: string) => str.toLowerCase())
            .value()
    }

    private getUnusedSyncstepKeys(): IExportAttribute['name'][] {
        const usedKeys = this.getMutableSyncstepConfigs().map(o => o.key())
        return _(this.availableSyncstepParameters)
            .map((o: IExportAttribute) => o.name)
            .filter((key: IExportAttribute['name']) => !usedKeys.find(u => u == key))
            .sortBy((str: string) => str.toLowerCase())
            .value()
    }

    private onAddConfigurationClick() {
        const c = new Config({
            companysyncstepid: this.data.id,
            id: 0,
            keyvalue: '',
            value: '',
        } as ICompanySyncstepParameter, this.availableSyncstepParameters, this.messageFlowOptions)
        this.mutableSyncstepConfigs.push(c)
        this.errorManager.addErrorObject(c.eo_key)
        this.errorManager.addErrorObject(c.eo_value)
    }

    private onDeleteConfigurationClick(c: Config) {
        if (Number(c.id)) {
            c.isDeleted(true)
        } else {
            this.mutableSyncstepConfigs.remove(c)
        }
        this.errorManager.removeErrorObject(c.eo_key)
        this.errorManager.removeErrorObject(c.eo_value)
    }

    private getMutableSyncstepConfigs(): Config[] {
        return this.mutableSyncstepConfigs().filter(o => !o.isDeleted())
    }

    override dispose() {
        super.dispose();
        this.mutableSyncstepConfigs().forEach(o => o.dispose())
    }

}
